// Agenda — today, then the next four weeks, one block per day.
//
// Phase 7 stage 3. Ported from Zanetto Tools, where it is the phone-native
// view and the default on a narrow screen. It is deliberately
// anchor-independent: it always starts today, because it answers "what is
// happening next" and a version you could scroll back into last March would
// just be the week view with extra steps.
//
// Empty days are skipped rather than drawn empty. A month grid has to show
// every square; a list does not, and four weeks of "nothing on" is noise
// between the two days that matter.
//
// **One card per JOB, not per booking** (2026-08-31, Nathan's call, matching
// Zanetto Tools). Until then this listed a row per booking with the RESOURCE
// in the strong text, which reads as a roster: an excavator that appears on
// three jobs looked like three unrelated lines, and the same job with five
// resources on it filled the day with five near-identical rows. Grouping by
// job puts the question the view is asked ("what work is on, and who is going
// to it") in one card, and is the shape DayPanel already uses for a day.
import { useMemo } from 'react';
import {
  formatClockRange,
  isWeekend,
  formatDate,
  todayISO,
  weekdayShort,
  type DateOnly,
} from '../../lib/dates';
import { jobColour } from '../../lib/jobColour';
import { groupDayByJob, type JobDayGroup } from '../../lib/jobGroups';
import { type NwLookup, type WorksOn } from '../../lib/nonWorkingDays';
import type { CalendarBooking, Resource, Unavailability } from '../../schedulingApi';

export function AgendaView({
  days,
  byDay,
  awayByDay,
  resources,
  onOpenDay,
  onSelectGroup,
  nwInfo,
  worksOn,
  emptyTitle = 'Nothing on for the next four weeks',
}: {
  days: DateOnly[];
  byDay: Map<string, CalendarBooking[]>;
  awayByDay: Map<string, Unavailability[]>;
  resources: Resource[];
  /** The day heading's count opens the whole-day panel. */
  onOpenDay: (date: DateOnly) => void;
  /** A card opens the per-job panel, where each booking has its own row. */
  onSelectGroup: (group: JobDayGroup, date: DateOnly) => void;
  nwInfo: NwLookup;
  worksOn: WorksOn;
  /** The week view reuses this list at phone width (DM5), over its own range —
      so the empty state cannot hard-code "the next four weeks". */
  emptyTitle?: string;
}) {
  const today = todayISO();
  const resourceName = (id: string) => resources.find((r) => r.id === id)?.name ?? 'A resource';

  const active = useMemo(
    () =>
      days
        .filter(
          (date) =>
            (byDay.get(date) ?? []).some((b) => worksOn(b, date)) ||
            (awayByDay.get(date)?.length ?? 0) > 0 ||
            // A NAMED day off earns its heading even with nothing booked —
            // the crew seeing "Christmas Day" is the feature. Plain weekends
            // stay skipped: four weeks of empty Saturdays is noise (ZT's rule).
            (nwInfo(date)?.dated ?? false)
        )
        .map((date) => ({
          date,
          // worksOn filters BEFORE grouping: a Friday–Tuesday job does not
          // occupy the weekend it spans, and a deliberate all-weekend job
          // still shows (the shared rule in lib/nonWorkingDays).
          cards: groupDayByJob((byDay.get(date) ?? []).filter((b) => worksOn(b, date))),
          away: awayByDay.get(date) ?? [],
          nw: nwInfo(date),
        })),
    [days, byDay, awayByDay, nwInfo, worksOn]
  );

  if (active.length === 0) {
    return (
      <div className="card p-10 text-center">
        <p className="text-sm font-medium text-gray-900">{emptyTitle}</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
          Bookings you make will show up here, soonest first.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5">
      {active.map(({ date, cards, away, nw }) => (
        <div key={date}>
          {/* The day is a HEADING, not a card wrapper. Zanetto Tools' shape,
              adopted 2026-08-31: one card per day with flush rows inside it
              made four weeks of work read as four grey slabs, because the
              only separation between two jobs was a hairline. Letting each
              job be its own rounded card — with the day as a label above the
              stack — is what makes a list of them look like a list. */}
          <div className="mb-2 flex items-baseline gap-2 px-0.5">
            <h3
              className={`text-sm font-bold ${
                date === today
                  ? 'text-brand-800'
                  : isWeekend(date)
                    ? 'text-gray-600'
                    : 'text-gray-900'
              }`}
            >
              {date === today ? 'Today' : weekdayShort(date)}
            </h3>
            <span className="text-xs text-gray-500">{formatDate(date)}</span>
            {nw?.dated && (
              <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-medium text-red-700">
                {nw.name}
              </span>
            )}
            <button
              type="button"
              onClick={() => onOpenDay(date)}
              className="ml-auto rounded px-1 text-xs text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-600"
            >
              {cards.length === 0 ? 'Nobody booked' : `${cards.length} on`}
            </button>
          </div>

          {cards.length === 0 && away.length === 0 && nw?.dated && (
            <p className="mb-2 px-0.5 text-xs text-gray-500">
              No work scheduled: {nw.name}.
            </p>
          )}

          <div className="space-y-2">
            {cards.map((card) => {
              const colour = jobColour(card.jobNumber, card.jobId, card.inConflict);
              return (
                <button
                  key={card.key}
                  type="button"
                  onClick={() => onSelectGroup(card, date)}
                  className="card flex w-full overflow-hidden text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
                >
                  {/* A solid full-height block, not a border-left: at 6px it
                      reads as part of the card rather than as an edge, and it
                      is the same fill the timeline bars use. */}
                  <div className={`w-1.5 flex-none ${colour.bar}`} aria-hidden="true" />

                  <div className="min-w-0 flex-1 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-base font-semibold text-gray-900">
                          {card.title}
                        </p>
                        {card.subtitle && (
                          <p className="truncate font-mono text-xs text-gray-500">
                            {card.subtitle}
                          </p>
                        )}
                      </div>
                      <p className="flex-none text-xs font-semibold tabular-nums text-gray-700">
                        {formatClockRange(card.startTime, card.endTime) ?? 'All day'}
                      </p>
                    </div>

                    {card.clientName && (
                      <p className="mt-1 truncate text-sm text-gray-600">{card.clientName}</p>
                    )}

                    {(card.crew.length > 0 || card.plantWithRego.length > 0) && (
                      <div className="mt-2 space-y-0.5 text-xs">
                        {card.crew.length > 0 && (
                          <div className="flex gap-2">
                            <span className="w-10 flex-none pt-px text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                              Crew
                            </span>
                            <span className="min-w-0 text-gray-700">{card.crew.join(', ')}</span>
                          </div>
                        )}
                        {card.plantWithRego.length > 0 && (
                          <div className="flex gap-2">
                            {/* gray-500, not the gray-400 Zanetto Tools uses: that
                                is 2.85:1 on white and every pair in this app has
                                been measured against AA since phase 7b. gray-500
                                is 4.83:1 — lighter than the value beside it, which
                                is the whole job of a label, and still legible. */}
                            <span className="w-10 flex-none pt-px text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                              Plant
                            </span>
                            <span className="min-w-0 text-gray-700">{card.plantWithRego.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {card.inConflict && (
                      <span className="mt-2 inline-block rounded-full bg-red-50 px-2 py-0.5 text-[0.65rem] font-medium text-red-700 ring-1 ring-inset ring-red-600/25">
                        Needs re-planning
                      </span>
                    )}
                  </div>
                </button>
              );
            })}

            {away.map((entry) => (
              <div
                key={entry.id}
                className="schedule-away rounded-lg border border-gray-200 px-4 py-2.5 shadow-sm"
              >
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{resourceName(entry.resourceId)}</span> away:{' '}
                  {entry.reason}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
